import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../redux/slices/authSlice';
import { apiHelper } from '../utils/api';
import { API_ENDPOINTS } from '../utils/constants';
import './VerifyEmail.scss';

const buildAppUser = (user) => {
  if (!user) return null;
  return {
    id: user._id || user.id || user.uid || '',
    fullName: user.name || user.displayName || '',
    email: user.email || '',
    avatar: user.photoURL || null,
    plan: user.plan || 'free',
    createdAt: user.createdAt,
    role: user.role || 'user',
    name: user.name || user.displayName || '',
    photoURL: user.photoURL || null,
    status: user.status || 'active',
  };
};

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'redirecting'
  const [message, setMessage] = useState('');
  const [sessionLimit, setSessionLimit] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('No verification token found. Please use the link from your email.');
      return;
    }

    let cancelled = false;
    let redirectTimer = null;

    const verify = async () => {
      try {
        const result = await apiHelper.get(API_ENDPOINTS.VERIFY_EMAIL(token));
        if (cancelled) return;
        // Auto-login: backend returns { token, user, autoLogin:true } on success.
        // Fallback (inactive/locked/old response): no token -> show Sign In button as before.
        if (result?.token && result?.user) {
          const appUser = buildAppUser(result.user);
          if (appUser) {
            dispatch(loginSuccess({ user: appUser, token: result.token }));
            setMessage('Email verified successfully! Signing you in…');
            setStatus('redirecting');
            redirectTimer = setTimeout(() => {
              if (!cancelled) navigate('/dashboard', { replace: true });
            }, 1200);
            return;
          }
        }
        setMessage(result.message || 'Email verified successfully!');
        setStatus('success');
      } catch (err) {
        if (cancelled) return;
        const code = err.response?.data?.code;
        const verified = err.response?.data?.verified === true;
        // Verified but session limit reached: verification succeeded,
        // user just needs to free a session via login page (existing flow).
        if (code === 'SESSION_LIMIT_REACHED' && verified) {
          setSessionLimit(true);
          setMessage(
            err.response?.data?.message ||
              'Email verified successfully! You already have active sessions. Please log out from another device and sign in.'
          );
          setStatus('success');
          return;
        }
        const msg =
          err.response?.data?.message ||
          'This verification link is invalid or has expired. Please request a new one.';
        setMessage(msg);
        setStatus('error');
      }
    };

    verify();
    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [searchParams, dispatch, navigate]);

  return (
    <div className="verify-email">
      <div className="verify-email__card">
        {/* Brand header */}
        <div className="verify-email__header">
          <div className="verify-email__logo-wrap">
            <span className="verify-email__logo-letter">M</span>
          </div>
          <h1 className="verify-email__brand">MIVENT</h1>
          <p className="verify-email__tagline">My Event Companion</p>
        </div>

        {/* Content area */}
        <div className="verify-email__body">
          {status === 'loading' && (
            <div className="verify-email__loading">
              <div className="verify-email__spinner" />
              <p>Verifying your email address…</p>
            </div>
          )}

          {status === 'redirecting' && (
            <div className="verify-email__success">
              <div className="verify-email__icon verify-email__icon--success">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2>Email Verified!</h2>
              <p>{message}</p>
              <p>Redirecting to your dashboard…</p>
            </div>
          )}

          {status === 'success' && (
            <div className="verify-email__success">
              <div className="verify-email__icon verify-email__icon--success">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2>Email Verified!</h2>
              <p>{message}</p>
              <Link
                to={sessionLimit ? '/login' : '/login?verified=true'}
                className="verify-email__btn"
              >
                {sessionLimit ? 'Go to Sign In to manage sessions' : 'Sign In to MIVENT'}
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="verify-email__error">
              <div className="verify-email__icon verify-email__icon--error">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M15 9l-6 6M9 9l6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h2>Verification Failed</h2>
              <p>{message}</p>
              <div className="verify-email__actions">
                <Link to="/login" className="verify-email__btn verify-email__btn--outline">
                  Back to Sign In
                </Link>
                <Link to="/register" className="verify-email__btn">
                  Create New Account
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
